/**
 * "Retry history" (plan 2026-09-27-003, U6): the LAST retry plus a count —
 * all `launcher_dispatch_log` keeps (`retry_count`, `last_retry_at`,
 * `last_retry_by_email`). A per-attempt history is a logged follow-up.
 *
 * Deliberately says nothing about WHAT a retry sent, and the columns give it
 * no way to: a retry of a Clay refusal rebuilds the message from the play as
 * it is now (sharing rules (m)), a retry of a failed START replays the stored
 * job, and the row records neither which arm ran nor the message each attempt
 * carried. "What was sent" is the surface that answers that, from the row's
 * own current copy — so it shows what the LATEST attempt posted, and this
 * panel must not imply otherwise.
 */
import { Text } from '@clay-run/glaze/text';

import { readAutoRetries } from '../../../dispatch-inspector-logic';
import type { PlaybooksDispatchLogRow } from '../../../schemas';

import { DetailPanel } from './DetailPanel';
import { formatSentAt } from './SendLogCard';

export function RetryHistoryPanel({ row }: { row: PlaybooksDispatchLogRow }) {
  const count = row.retry_count ?? 0;
  // Terra's own retries after a network error inside Clay (sharing rules (o)).
  const auto = readAutoRetries(row.result_data);
  const lastAuto = auto.at(-1);
  const by = row.last_retry_by_email ?? null;
  const at = row.last_retry_at ?? null;
  const last = [by === null ? 'Retried' : `Retried by ${by}`, at === null ? null : formatSentAt(at)]
    .filter((part): part is string => part !== null)
    .join(' · ');
  return (
    <DetailPanel title="Retry history">
      <div className="flex flex-col gap-1">
        <Text size="xs" variant="secondary">
          {last}
        </Text>
        {count > 1 && (
          <Text size="xs" variant="tertiary">
            {`Retried ${String(count)} times in all`}
          </Text>
        )}
        {lastAuto !== undefined && (
          <Text size="xs" variant="tertiary">
            {`Terra retried this on its own ${auto.length === 1 ? 'once' : `${String(auto.length)} times`} after network errors in Clay · last ${formatSentAt(lastAuto.at)}`}
          </Text>
        )}
      </div>
    </DetailPanel>
  );
}
